import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import ValidatedTextField from '../../Components/ValidatedTextField';
import ImageSelectorButton from '../../Components/ImageSelectorButton';
import Question from './Question';
import QuestionList from './QuestionList';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getTrainingDetail,
  getTrainingTopics,
  getTrainingCategory,
  updateTraining,
} from '../../endpoints/training';
import { nameValidator } from '../../utils/validators';
import AlertItem from '../../Components/AlertItem';
import TrainingCategorySelector from '../../Components/TrainingCategorySelector';
import TrainingTopicSelector from '../../Components/TrainingTopicSelector';
import TrainingGroupSelector from '../../Components/TrainingGroupSelector';
import {
  TrainingCategory,
  TrainingTopic,
  TrainingGroup,
  TrainingQuestion,
  TrainingCreateRequest,
  Training,
} from '../../types/training';
import { GroupPermission } from '../../types/group';
import GroupPermissionsField from '../../Components/GroupPermissionsField';

const REQUIRED_FIELD_IDS = [
  'training-name',
  'training-description',
  'training-video-link',
  'training-transcript',
  'training-summary',
] as const;

const TrainingEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const initializedRef = useRef(false);

  const {
    data: detailData,
    isLoading: detailLoading,
    error: detailError,
  } = useApiGet(
    ['training-detail-edit', id],
    () => getTrainingDetail({ id: id! }),
    { enabled: !!id }
  );
  const { data: topicsData } = useApiGet(
    ['training-topics-edit'],
    getTrainingTopics,
    { enabled: !!id }
  );
  const { data: categoriesData } = useApiGet(
    ['training-categories-edit'],
    getTrainingCategory,
    { enabled: !!id }
  );

  const detail: Training | undefined =
    detailData && 'data' in detailData
      ? (detailData as { data: Training }).data
      : (detailData as unknown as Training);
  const topicsList = useMemo(
    () => (topicsData as { data?: TrainingTopic[] })?.data ?? [],
    [topicsData]
  );
  const categoriesList = useMemo(
    () => (categoriesData as { data?: TrainingCategory[] })?.data ?? [],
    [categoriesData]
  );

  // Form state
  const [trainingName, setTrainingName] = useState('');
  const [nameIsValid, setNameIsValid] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [trainingDescription, setTrainingDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [trainingImage, setTrainingImage] = useState<File | null>(null);
  const [videoLink, setVideoLink] = useState('');
  const [videoLinkError, setVideoLinkError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [summary, setSummary] = useState('');
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);
  const [addQuestion, setAddQuestion] = useState(false);
  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [topics, setTopics] = useState<TrainingTopic[]>([]);
  const [group, setGroup] = useState<TrainingGroup | null>(null);
  const [createGroup, setCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createTopic, setCreateTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [createCategory, setCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [trainingActive, setTrainingActive] = useState(true);
  const [groupPermissions, setGroupPermissions] = useState<GroupPermission[]>(
    []
  );

  useEffect(() => {
    if (!detail || initializedRef.current) return;
    initializedRef.current = true;
    setTrainingName(detail.name ?? '');
    setTrainingDescription(detail.description ?? '');
    setVideoLink(detail.video_url ?? '');
    setSummary(detail.summary ?? '');
    setTranscript(detail.transcript ?? '');
    setTrainingActive(
      typeof detail.is_active === 'boolean' ? detail.is_active : true
    );
    if (detail.questions?.length) {
      setQuestions(
        detail.questions.map((q: any) => ({
          question: q.question,
          answer: q.answer,
          order: q.order ?? 0,
          selection_a: q.selection_a ?? '',
          selection_b: q.selection_b ?? '',
          selection_c: q.selection_c ?? '',
          selection_d: q.selection_d ?? '',
          selection_e: q.selection_e ?? '',
        }))
      );
    }
    if (detail.topics?.length) {
      const first = detail.topics[0];
      if (typeof first === 'object' && first && 'name' in first) {
        setTopics(detail.topics as { id: string; name: string }[]);
      } else {
        setTopics(
          topicsList.length
            ? topicsList.filter((t) =>
                (detail.topics as string[])!.includes(t.id)
              )
            : (detail.topics as string[])!.map((tid) => ({
                id: tid,
                name: tid,
              }))
        );
      }
    }
    if (detail.categories?.length) {
      const first = detail.categories[0];
      if (typeof first === 'object' && first && 'name' in first) {
        setCategories(detail.categories as { id: string; name: string }[]);
      } else {
        setCategories(
          categoriesList.length
            ? categoriesList.filter((c) =>
                (detail.categories as string[])!.includes(c.id)
              )
            : (detail.categories as string[])!.map((cid) => ({
                id: cid,
                name: cid,
              }))
        );
      }
    }
    if (detail.group_permissions?.length) {
      setGroupPermissions(
        detail.group_permissions.map((p) => ({
          group_type_id: p.group_type_id,
          all_roles: p.all_roles,
          role_ids: p.all_roles ? undefined : [...(p.role_ids || [])],
        }))
      );
    } else {
      setGroupPermissions([]);
    }
  }, [detail, topicsList, categoriesList]);

  const { mutate, isPending } = useApiSend(
    (data: TrainingCreateRequest) => updateTraining(id!, data),
    () => navigate(`/training/${id}`),
    (error: any) => {
      const errorMessage =
        error?.message ||
        error?.error ||
        'Error updating training. Please try again later.';
      setApiError(errorMessage);
    }
  );

  const handleNameChange = (isValid: boolean, value: string) => {
    setNameIsValid(!isValid);
    setTrainingName(value);
    setNameError(value.length > 255);
  };
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTrainingDescription(value);
    if (value.length > 255)
      setDescriptionError('Description must be less than 255 characters');
    else if (value.length === 0) setDescriptionError('Description is required');
    else setDescriptionError(null);
  };
  const handleVideoLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVideoLink(value);
    setVideoLinkError(value.length === 0 ? 'Video Link is required' : null);
  };
  const handleTranscriptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTranscript(value);
    setTranscriptError(value.length === 0 ? 'Transcript is required' : null);
  };
  const handleSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSummary(value);
    setSummaryError(value.length === 0 ? 'Summary is required' : null);
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) setTrainingImage(file);
  };
  const handleQuestionUpdate = (updatedQuestions: TrainingQuestion[]) => {
    setQuestions(
      updatedQuestions.map((q, index) => ({ ...q, order: index + 1 }))
    );
  };
  const handleAddQuestion = (question: TrainingQuestion) => {
    setQuestions([...questions, { ...question, order: questions.length + 1 }]);
    setAddQuestion(false);
  };

  const buildRequest = (): TrainingCreateRequest => {
    const ret: TrainingCreateRequest = {
      name: trainingName,
      description: trainingDescription,
      video_url: videoLink,
      summary: summary,
      transcript: transcript,
    };
    const tempCategories = categories.map((c) => c.id);
    if (createCategory && newCategoryName) tempCategories.push(newCategoryName);
    if (tempCategories.length > 0) ret.categories = tempCategories;
    const tempTopics = topics.map((t) => t.id);
    if (createTopic && newTopicName) tempTopics.push(newTopicName);
    if (tempTopics.length > 0) ret.topics = tempTopics;
    if (group) ret.group = { id: group.id, step: 1 };
    if (createGroup && newGroupName)
      ret.group = { name: newGroupName, step: 1 };
    if (questions.length > 0) ret.questions = questions;
    ret.is_active = trainingActive;
    ret.group_permissions = groupPermissions
      .filter(
        (p) =>
          p.group_type_id &&
          (p.all_roles || (p.role_ids && p.role_ids.length > 0))
      )
      .map((p) => ({
        group_type_id: p.group_type_id,
        all_roles: p.all_roles,
        role_ids: p.all_roles ? undefined : p.role_ids,
      }));
    return ret;
  };

  const getMissingFieldLabels = (): string[] => {
    const missing: string[] = [];
    if (trainingName.length === 0 || nameIsValid || nameError)
      missing.push('Name');
    if (trainingDescription.length === 0 || descriptionError)
      missing.push('Description');
    if (videoLink.length === 0 || videoLinkError) missing.push('Video link');
    if (transcript.length === 0 || transcriptError) missing.push('Transcript');
    if (summary.length === 0 || summaryError) missing.push('Summary');
    return missing;
  };

  const getFirstInvalidFieldId = (): string | null => {
    const invalid = [
      trainingName.length === 0 || nameIsValid || nameError,
      !!(trainingDescription.length === 0 || descriptionError),
      !!(videoLink.length === 0 || videoLinkError),
      !!(transcript.length === 0 || transcriptError),
      !!(summary.length === 0 || summaryError),
    ];
    const idx = invalid.findIndex(Boolean);
    return idx >= 0 ? REQUIRED_FIELD_IDS[idx] : null;
  };

  const validateForm = (): boolean => {
    let valid = true;
    if (trainingName.length === 0) {
      setNameError(true);
      valid = false;
    }
    if (trainingDescription.length === 0) {
      setDescriptionError('Description is required');
      valid = false;
    }
    if (videoLink.length === 0) {
      setVideoLinkError('Video Link is required');
      valid = false;
    }
    if (transcript.length === 0) {
      setTranscriptError('Transcript is required');
      valid = false;
    }
    if (summary.length === 0) {
      setSummaryError('Summary is required');
      valid = false;
    }
    if (
      nameIsValid ||
      nameError ||
      descriptionError ||
      videoLinkError ||
      transcriptError ||
      summaryError
    ) {
      valid = false;
    }
    return valid;
  };

  const submitTraining = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setApiError(null);
    if (!validateForm()) {
      const missing = getMissingFieldLabels();
      const errorMessage =
        missing.length > 0
          ? `Please complete the required fields. Missing: ${missing.join(', ')}.`
          : 'Please complete all required fields before submitting.';
      setValidationError(errorMessage);
      const firstInvalidId = getFirstInvalidFieldId();
      if (firstInvalidId) {
        setTimeout(() => {
          const el = document.getElementById(firstInvalidId!);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (
            el?.querySelector('input') ?? el?.querySelector('textarea')
          )?.focus();
        }, 100);
      }
      return;
    }
    mutate(buildRequest());
  };

  if (detailLoading || !id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detailError || !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading training. It may not exist or you may not have access."
          onClose={() => {}}
          keepOpen={true}
        />
        <Button onClick={() => navigate('/training')}>Back to Training</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            component="h1"
            textAlign="center"
            color="primary"
            gutterBottom
          >
            Edit Training
          </Typography>
        </Grid>

        {/* Card 1 – Basic Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Basic Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box id="training-name">
                    <ValidatedTextField
                      label="Training Name"
                      value={trainingName}
                      validator={nameValidator}
                      valueRequired={true}
                      errorOverride={
                        nameError
                          ? 'Name must be less than 255 characters'
                          : null
                      }
                      onChange={handleNameChange}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="training-description"
                    label="Description"
                    required
                    InputLabelProps={{ required: true }}
                    size="small"
                    fullWidth
                    multiline
                    helperText={descriptionError ?? undefined}
                    error={!!descriptionError}
                    rows={4}
                    value={trainingDescription}
                    onChange={handleDescriptionChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={trainingActive}
                        onChange={(e) => setTrainingActive(e.target.checked)}
                        id="training-is-active"
                        color="primary"
                      />
                    }
                    label="Active (visible in training catalog)"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Inactive trainings are hidden from the list for most users;
                    you can return here to reactivate.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2 – Content & Media */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Content & Media
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    {trainingImage ? (
                      <>
                        <Typography variant="h6" gutterBottom color="primary">
                          Logo Preview
                        </Typography>
                        <Box
                          component="img"
                          sx={{
                            height: 100,
                            width: 'auto',
                            maxWidth: 100,
                            borderRadius: '10px',
                            mb: 2,
                          }}
                          alt="Training logo"
                          src={URL.createObjectURL(trainingImage)}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => setTrainingImage(null)}
                        >
                          Clear
                        </Button>
                      </>
                    ) : (
                      <ImageSelectorButton
                        label="Training Image"
                        handleFileUpload={handleFileUpload}
                      />
                    )}
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="training-video-link"
                    label="Video Link"
                    required
                    InputLabelProps={{ required: true }}
                    size="small"
                    fullWidth
                    helperText={videoLinkError ?? undefined}
                    error={!!videoLinkError}
                    value={videoLink}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VideoIcon />
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleVideoLinkChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="training-transcript"
                    label="Video Transcript"
                    required
                    InputLabelProps={{ required: true }}
                    size="small"
                    fullWidth
                    multiline
                    helperText={transcriptError ?? undefined}
                    error={!!transcriptError}
                    rows={4}
                    value={transcript}
                    onChange={handleTranscriptChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="training-summary"
                    label="Summary"
                    required
                    InputLabelProps={{ required: true }}
                    size="small"
                    fullWidth
                    multiline
                    helperText={summaryError ?? undefined}
                    error={!!summaryError}
                    rows={4}
                    value={summary}
                    onChange={handleSummaryChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3 – Quiz Questions (optional) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Quiz Questions (optional)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} textAlign="center">
                  {!addQuestion && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setAddQuestion(true)}
                    >
                      Add Question
                    </Button>
                  )}
                  {addQuestion && (
                    <Question
                      onAddQuestion={handleAddQuestion}
                      onCancel={() => setAddQuestion(false)}
                    />
                  )}
                </Grid>
                {questions.length > 0 && (
                  <Grid item xs={12}>
                    <QuestionList
                      questions={questions}
                      onRemoveQuestion={(index) =>
                        setQuestions(questions.filter((_, i) => i !== index))
                      }
                      onQuestionOrderUpdate={handleQuestionUpdate}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4 – Taxonomy & Settings (optional) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Taxonomy & Settings (optional)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TrainingCategorySelector
                    value={categories}
                    handleValueChange={(value) => setCategories(value)}
                    label="Training Category"
                  />
                  {createCategory ? (
                    <TextField
                      id="new-category"
                      label="New Category"
                      size="small"
                      fullWidth
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  ) : (
                    <Button
                      size="small"
                      onClick={() => setCreateCategory(true)}
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                    >
                      Create New Category
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TrainingTopicSelector
                    value={topics}
                    handleValueChange={(value) => setTopics(value)}
                    label="Training Topic"
                  />
                  {createTopic ? (
                    <TextField
                      id="new-topic"
                      label="New Topic"
                      size="small"
                      fullWidth
                      value={newTopicName}
                      onChange={(e) => setNewTopicName(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  ) : (
                    <Button
                      size="small"
                      onClick={() => setCreateTopic(true)}
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                    >
                      Create New Topic
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TrainingGroupSelector
                    value={group}
                    handleValueUpdate={(value) => setGroup(value)}
                    label="Training Series"
                  />
                  {createGroup ? (
                    <TextField
                      id="new-series"
                      label="New Series"
                      size="small"
                      fullWidth
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  ) : (
                    <Button
                      size="small"
                      onClick={() => setCreateGroup(true)}
                      startIcon={<AddIcon />}
                      sx={{ mt: 2 }}
                    >
                      Create New Series
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 5 – Who can view */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Who can view this training
              </Typography>
              <GroupPermissionsField
                value={groupPermissions}
                onChange={setGroupPermissions}
                helperText="Leave empty to allow all signed-in users. Add group types to restrict who can view this training."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Error alert above buttons */}
        {(validationError || apiError) && (
          <Grid item xs={12}>
            <AlertItem
              severity="error"
              description={validationError || apiError || ''}
              onClose={() => {
                setValidationError(null);
                setApiError(null);
              }}
              keepOpen={true}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => navigate('/training')}>Cancel</Button>
            <LoadingButton
              loading={isPending}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={submitTraining}
            >
              Update Training
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrainingEdit;
